/**
 * Shopify Collection Infinite Scroll v2
 * Based on best practices research for Shopify themes
 */

class InfiniteScroll {
  constructor() {
    this.currentPage = 1;
    this.totalPages = 1;
    this.loading = false;
    this.observer = null;
    this.triggerElement = null;

    // Selectors
    this.selectors = {
      productsContainer: "#CollectionLoop",
      loadingIndicator: ".grid__loader",
      paginationNext: ".pagination__next, .pagination-custom__next",
      infiniteScrollSection: '[data-infinite-scroll="true"]',
      resultCount: "[data-results-count]",
    };

    this.init();
  }

  init() {
    // Get initial pagination data
    const section = document.querySelector(
      this.selectors.infiniteScrollSection
    );
    if (!section) {
      console.log("[Infinite Scroll] Section not found");
      return;
    }

    // Read pagination data from DOM first
    const domCurrentPage = parseInt(section.getAttribute("data-current-page"));
    const domTotalPages = parseInt(section.getAttribute("data-pages"));

    // Check if we have fresh window.paginationData (it gets updated when filters change)
    const hasFreshWindowData =
      window.paginationData &&
      window.paginationData.currentPage === 1 &&
      window.paginationData.totalPages > 0;

    if (hasFreshWindowData) {
      // Use fresh window data after filter change
      this.currentPage = window.paginationData.currentPage;
      this.totalPages = window.paginationData.totalPages;
      console.log("[Infinite Scroll] Using fresh window.paginationData");
    } else if (domCurrentPage && domTotalPages) {
      // Use DOM attributes
      this.currentPage = domCurrentPage;
      this.totalPages = domTotalPages;
      console.log("[Infinite Scroll] Using DOM attributes");
    } else {
      // Fallback
      this.currentPage = 1;
      this.totalPages = 1;
      console.log("[Infinite Scroll] Using fallback values");
    }

    console.log(
      `[Infinite Scroll] Initialized - Page ${this.currentPage}/${this.totalPages}`,
      {
        domData: { currentPage: domCurrentPage, totalPages: domTotalPages },
        windowData: window.paginationData,
        filters: new URL(window.location.href).searchParams.toString(),
      }
    );

    // Only set up if there are more pages
    if (this.hasMorePages()) {
      this.setupIntersectionObserver();
    }
  }

  hasMorePages() {
    return this.currentPage < this.totalPages;
  }

  setupIntersectionObserver() {
    // Clean up existing observer
    if (this.observer) {
      this.observer.disconnect();
    }

    // Create trigger element at the bottom of products
    const productsContainer = document.querySelector(
      this.selectors.productsContainer
    );
    if (!productsContainer) return;

    // Remove existing trigger if any
    if (this.triggerElement) {
      this.triggerElement.remove();
    }

    // Create new trigger element
    this.triggerElement = document.createElement("div");
    this.triggerElement.className = "infinite-scroll-trigger";
    this.triggerElement.style.height = "1px";
    this.triggerElement.setAttribute("aria-hidden", "true");
    productsContainer.parentElement.appendChild(this.triggerElement);

    // Set up intersection observer
    const options = {
      root: null,
      rootMargin: "300px", // Start loading 300px before trigger is visible
      threshold: 0,
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.loading && this.hasMorePages()) {
          this.loadNextPage();
        }
      });
    }, options);

    this.observer.observe(this.triggerElement);
  }

  async loadNextPage() {
    if (this.loading || !this.hasMorePages()) return;

    this.loading = true;
    const nextPage = this.currentPage + 1;

    // Show loading indicator
    const loadingIndicator = document.querySelector(
      this.selectors.loadingIndicator
    );
    if (loadingIndicator) {
      loadingIndicator.style.display = "block";
    }

    console.log(
      `[Infinite Scroll] Loading page ${nextPage}/${this.totalPages}`
    );

    try {
      // Build URL with current filters
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set("page", nextPage);

      // Fetch the next page
      const response = await fetch(currentUrl.toString());
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      // Extract products from response
      const newProductsContainer = doc.querySelector(
        this.selectors.productsContainer
      );
      const productsContainer = document.querySelector(
        this.selectors.productsContainer
      );

      if (newProductsContainer && productsContainer) {
        // Append new products
        const newProducts = newProductsContainer.innerHTML;
        productsContainer.insertAdjacentHTML("beforeend", newProducts);

        // Update current page
        this.currentPage = nextPage;

        // Check if we need to continue observing
        if (!this.hasMorePages()) {
          console.log("[Infinite Scroll] All pages loaded");
          this.cleanup();
        }

        // Trigger custom event for other scripts
        document.dispatchEvent(
          new CustomEvent("infinitescroll:loaded", {
            detail: { page: this.currentPage, totalPages: this.totalPages },
          })
        );
      }
    } catch (error) {
      console.error("[Infinite Scroll] Error loading page:", error);
    } finally {
      this.loading = false;
      if (loadingIndicator) {
        loadingIndicator.style.display = "none";
      }
    }
  }

  cleanup() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.triggerElement) {
      this.triggerElement.remove();
      this.triggerElement = null;
    }
  }

  refreshPaginationData() {
    // Force re-read pagination data from current page
    const section = document.querySelector(
      this.selectors.infiniteScrollSection
    );
    if (!section) return false;

    // Try to get fresh data from a script tag or window object
    const scripts = document.querySelectorAll("script");
    for (let script of scripts) {
      const content = script.textContent;
      if (content.includes("window.paginationData")) {
        try {
          // Extract pagination data from script
          eval(content);
          console.log(
            "[Infinite Scroll] Refreshed pagination data from script"
          );
          return true;
        } catch (e) {
          console.error(
            "[Infinite Scroll] Error parsing pagination script:",
            e
          );
        }
      }
    }

    return false;
  }

  destroy() {
    this.cleanup();
    console.log("[Infinite Scroll] Destroyed");
  }
}

// Initialize on DOM ready
let infiniteScrollInstance = null;

function initializeInfiniteScroll() {
  // Clean up existing instance
  if (infiniteScrollInstance) {
    infiniteScrollInstance.destroy();
  }

  // Check if infinite scroll is enabled
  if (document.querySelector('[data-infinite-scroll="true"]')) {
    infiniteScrollInstance = new InfiniteScroll();
  }
}

// Initialize on page load
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeInfiniteScroll);
} else {
  initializeInfiniteScroll();
}

// Handle filter changes - monitor URL changes
let lastUrl = window.location.href;
let checkInterval = null;

function checkForFilterChange() {
  const currentUrl = window.location.href;
  const lastParams = new URL(lastUrl).searchParams;
  const currentParams = new URL(currentUrl).searchParams;

  // Remove page parameter for comparison
  lastParams.delete("page");
  currentParams.delete("page");

  // Check if filters have changed
  if (lastParams.toString() !== currentParams.toString()) {
    console.log("[Infinite Scroll] Filters changed, waiting for DOM update...");
    lastUrl = currentUrl;

    // Clear window.paginationData to force fresh read
    window.paginationData = null;

    // Wait for DOM to be updated by the filter system
    let attempts = 0;
    const maxAttempts = 20; // 2 seconds max wait

    const waitForUpdate = setInterval(() => {
      attempts++;

      // Check if window.paginationData has been updated
      if (window.paginationData && window.paginationData.currentPage === 1) {
        clearInterval(waitForUpdate);
        console.log("[Infinite Scroll] DOM updated, reinitializing...");
        initializeInfiniteScroll();
      } else if (attempts >= maxAttempts) {
        clearInterval(waitForUpdate);
        console.log(
          "[Infinite Scroll] Timeout waiting for update, reinitializing anyway..."
        );
        initializeInfiniteScroll();
      }
    }, 100);
  }
}

// Monitor for filter changes
checkInterval = setInterval(checkForFilterChange, 500);

// Also listen for popstate (browser back/forward)
window.addEventListener("popstate", () => {
  setTimeout(checkForFilterChange, 100);
});

// Listen for custom events that might indicate filter updates
document.addEventListener("collection:updated", () => {
  console.log("[Infinite Scroll] Collection updated event received");
  setTimeout(initializeInfiniteScroll, 100);
});

// Watch for changes to the products container (filter updates)
const productsGrid = document.querySelector("[data-products-grid]");
if (productsGrid) {
  const observer = new MutationObserver((mutations) => {
    // Check if this is a major change (filter update)
    let totalNodesChanged = 0;
    mutations.forEach((mutation) => {
      totalNodesChanged +=
        mutation.addedNodes.length + mutation.removedNodes.length;
    });

    // If many nodes changed at once, it's likely a filter update
    if (totalNodesChanged > 10) {
      console.log(
        "[Infinite Scroll] Major DOM change detected, likely filter update"
      );
      // Wait for any pagination script to execute
      setTimeout(() => {
        checkForFilterChange();
      }, 100);
    }
  });

  observer.observe(productsGrid, {
    childList: true,
    subtree: true,
  });
}

// Export for debugging
window.infiniteScrollInstance = infiniteScrollInstance;
window.refreshInfiniteScroll = initializeInfiniteScroll;
